import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Barcode, Package, Pencil, Plus, Power } from 'lucide-react';
import {
  FRAME_LIKE_TYPES,
  PERMISSIONS,
  ProductStatus,
  type ProductVariantSummary,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { LoadingState } from '@/components/ui/LoadingState';
import { PageHeader } from '@/components/ui/PageHeader';
import { useLocale } from '@/hooks/useLocale';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorMessage } from '@/services/apiClient';
import { formatDate } from '@/utils/format';
import { useChangeProductStatusMutation, useProductQuery } from '../api/catalog.api';
import { ProductFormDrawer } from '../components/ProductFormDrawer';
import { ProductStatusBadge } from '../components/ProductStatusBadge';
import { ProductTypeBadge } from '../components/ProductTypeBadge';
import { PhotoGallery } from '../components/PhotoGallery';
import { VariantCard } from '../components/VariantCard';
import { VariantFormDrawer } from '../components/VariantFormDrawer';
import { localizedName } from '../category-label';
import { labelLine, type LabelBuilderState } from '../labels/labelLines';

export function ProductDetailPage() {
  const { t, i18n } = useTranslation();
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { locale, isRtl } = useLocale();

  const [isEditOpen, setEditOpen] = useState(false);
  const [variantDrawer, setVariantDrawer] = useState<
    { mode: 'add' } | { mode: 'edit'; variant: ProductVariantSummary } | null
  >(null);
  const [isStatusDialogOpen, setStatusDialogOpen] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);

  const productQuery = useProductQuery(productId);
  const changeStatus = useChangeProductStatusMutation(productId ?? '');

  const canEdit = can(PERMISSIONS.CATALOG_EDIT);
  const canChangeStatus = can(PERMISSIONS.CATALOG_CHANGE_STATUS);

  if (productQuery.isPending) return <LoadingState />;

  if (productQuery.isError || !productQuery.data) {
    return (
      <div className="space-y-4">
        <Alert tone="error">
          {apiErrorMessage(productQuery.error) ?? t('catalog.products.notFound')}
        </Alert>
        <Button variant="secondary" onClick={() => void navigate('/inventory/products')}>
          {t('catalog.products.backToList')}
        </Button>
      </div>
    );
  }

  const product = productQuery.data;
  const isActive = product.status === ProductStatus.ACTIVE;
  const labelVariants = product.variants.filter((variant) => variant.isActive);

  const openLabels = (variants: ProductVariantSummary[]) => {
    const state: LabelBuilderState = {
      lines: variants.map((variant) => labelLine(variant, product)),
    };
    void navigate('/inventory/labels', { state });
  };
  const BackIcon = isRtl ? ArrowRight : ArrowLeft;

  const confirmStatusChange = () => {
    setStatusError(null);

    changeStatus.mutate(
      { status: isActive ? ProductStatus.INACTIVE : ProductStatus.ACTIVE },
      {
        onSuccess: () => {
          setStatusDialogOpen(false);
          setFeedback(
            isActive ? t('catalog.products.deactivated') : t('catalog.products.reactivated'),
          );
        },
        onError: (caught) =>
          setStatusError(apiErrorMessage(caught) ?? t('catalog.products.statusError')),
      },
    );
  };

  return (
    <div className="space-y-6">
      <Link
        to="/inventory/products"
        className="text-muted hover:text-ink inline-flex items-center gap-1.5 text-sm"
      >
        <BackIcon className="size-4" aria-hidden />
        {t('catalog.products.backToList')}
      </Link>

      <PageHeader
        title={product.name}
        subtitle={product.productNumber}
        actions={
          <>
            <ProductTypeBadge type={product.type} />
            <ProductStatusBadge status={product.status} />

            {labelVariants.length > 0 ? (
              <Button variant="secondary" onClick={() => openLabels(labelVariants)}>
                <Barcode className="size-4" aria-hidden />
                {t('catalog.labels.printLabels')}
              </Button>
            ) : null}

            {canEdit ? (
              <Button variant="secondary" onClick={() => setEditOpen(true)}>
                <Pencil className="size-4" aria-hidden />
                {t('catalog.products.edit')}
              </Button>
            ) : null}

            {canChangeStatus ? (
              <Button
                variant={isActive ? 'danger' : 'primary'}
                onClick={() => setStatusDialogOpen(true)}
              >
                <Power className="size-4" aria-hidden />
                {isActive ? t('catalog.products.deactivate') : t('catalog.products.reactivate')}
              </Button>
            ) : null}
          </>
        }
      />

      {feedback ? <Alert tone="success">{feedback}</Alert> : null}
      {statusError ? <Alert tone="error">{statusError}</Alert> : null}
      {!isActive ? <Alert tone="warning">{t('catalog.products.inactiveNotice')}</Alert> : null}

      <Card>
        <CardHeader title={t('catalog.sections.details')} />
        <dl className="grid grid-cols-2 gap-4 p-5 sm:grid-cols-4">
          <Detail label={t('catalog.fields.brand')} value={product.brandName} />
          <Detail
            label={t('catalog.fields.category')}
            value={
              product.categoryName
                ? localizedName(product.categoryName, product.categoryNameAr, i18n.language)
                : null
            }
          />
          {FRAME_LIKE_TYPES.includes(product.type) ? (
            <Detail
              label={t('catalog.fields.lensInclusion')}
              value={
                product.lensInclusion
                  ? t(`catalog.lensInclusion.${product.lensInclusion}`)
                  : t('catalog.fields.notSpecified')
              }
            />
          ) : null}
          <Detail label={t('catalog.fields.nameAr')} value={product.nameAr} rtl />
          <Detail label={t('catalog.fields.variants')} value={String(product.variantCount)} ltr />
          <Detail
            label={t('catalog.fields.created')}
            value={formatDate(product.createdAt, locale)}
          />
          {product.description ? (
            <div className="col-span-2 sm:col-span-4">
              <dt className="text-muted text-xs">{t('catalog.fields.description')}</dt>
              {/* Free text that may be in either language: let the browser infer direction from the first strong character, or an English sentence in an Arabic layout has its full stop dragged to the front. */}
              <dd dir="auto" className="text-ink mt-0.5 text-sm">
                {product.description}
              </dd>
            </div>
          ) : null}
        </dl>
      </Card>

      <Card>
        <CardHeader
          title={t('catalog.media.productPhotos')}
          subtitle={t('catalog.media.productPhotosHint')}
        />
        <div className="p-5">
          <PhotoGallery
            productId={product.id}
            variantId={null}
            photos={product.media.filter((photo) => photo.variantId === null)}
            canEdit={canEdit}
            subject={product.name}
            onFeedback={setFeedback}
          />
        </div>
      </Card>

      <Card>
        <CardHeader
          title={t('catalog.sections.variants')}
          subtitle={t('catalog.sections.variantsHint')}
          action={
            canEdit ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setVariantDrawer({ mode: 'add' })}
              >
                <Plus className="size-4" aria-hidden />
                {t('catalog.variants.add')}
              </Button>
            ) : undefined
          }
        />

        {product.variants.length === 0 ? (
          <EmptyState
            icon={Package}
            title={t('catalog.variants.empty')}
            description={t('catalog.variants.emptyHint')}
            className="px-5 py-10"
          />
        ) : (
          <div className="space-y-4 p-5">
            {product.variants.map((variant) => (
              <VariantCard
                key={variant.id}
                productId={product.id}
                productType={product.type}
                variant={variant}
                photos={product.media.filter((photo) => photo.variantId === variant.id)}
                canEdit={canEdit}
                onEdit={() => setVariantDrawer({ mode: 'edit', variant })}
                onPrintLabels={variant.isActive ? () => openLabels([variant]) : undefined}
                onFeedback={setFeedback}
              />
            ))}
          </div>
        )}
      </Card>

      {canEdit ? (
        <>
          <ProductFormDrawer
            isOpen={isEditOpen}
            onClose={() => setEditOpen(false)}
            product={product}
            onSaved={(_saved, message) => setFeedback(message)}
          />

          <VariantFormDrawer
            productId={product.id}
            productType={product.type}
            variant={variantDrawer?.mode === 'edit' ? variantDrawer.variant : undefined}
            isOpen={variantDrawer !== null}
            onClose={() => setVariantDrawer(null)}
            onSaved={setFeedback}
          />
        </>
      ) : null}

      <ConfirmDialog
        isOpen={isStatusDialogOpen}
        onCancel={() => setStatusDialogOpen(false)}
        onConfirm={confirmStatusChange}
        isConfirming={changeStatus.isPending}
        isDestructive={isActive}
        title={
          isActive ? t('catalog.products.deactivateTitle') : t('catalog.products.reactivateTitle')
        }
        message={
          isActive
            ? t('catalog.products.deactivateMessage')
            : t('catalog.products.reactivateMessage')
        }
        confirmLabel={
          isActive ? t('catalog.products.deactivate') : t('catalog.products.reactivate')
        }
      />
    </div>
  );
}

function Detail({
  label,
  value,
  ltr = false,
  rtl = false,
}: {
  label: string;
  value: string | null;
  ltr?: boolean;
  rtl?: boolean;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-muted text-xs">{label}</dt>
      <dd
        className="text-ink mt-0.5 truncate text-sm"
        {...(ltr ? { dir: 'ltr' as const } : rtl ? { dir: 'rtl' as const } : {})}
      >
        {value && value.trim() ? value : '—'}
      </dd>
    </div>
  );
}
